/**
 * Chat List Slice
 *
 * Manages state for the sidebar chat sessions list.
 * Handles paginated loading and appending of chat history entries.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ChatListItem } from '@contracts';

// ============================================
// State Types
// ============================================

export interface ChatListState {
  chats: ChatListItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  nextCursor: number | null;
  /** Prevents re-fetching if the list is already loaded for the current session */
  isLoaded: boolean;
}

// ============================================
// Initial State
// ============================================

export const initialChatListState: ChatListState = {
  chats: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  hasMore: false,
  nextCursor: null,
  isLoaded: false,
};

// ============================================
// Slice
// ============================================

const chatListSlice = createSlice({
  name: 'chat/chatList',
  initialState: initialChatListState,
  reducers: {
    setChatListLoading: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    setChatListLoadingMore: (state) => {
      state.isLoadingMore = true;
    },

    setChatList: (
      state,
      action: PayloadAction<{
        chats: ChatListItem[];
        hasMore: boolean;
        nextCursor: number | null;
      }>,
    ) => {
      state.chats = action.payload.chats;
      state.hasMore = action.payload.hasMore;
      state.nextCursor = action.payload.nextCursor;
      state.isLoading = false;
      state.isLoaded = true;
      state.error = null;
    },

    appendChatList: (
      state,
      action: PayloadAction<{
        chats: ChatListItem[];
        hasMore: boolean;
        nextCursor: number | null;
      }>,
    ) => {
      state.chats = [...state.chats, ...action.payload.chats];
      state.hasMore = action.payload.hasMore;
      state.nextCursor = action.payload.nextCursor;
      state.isLoadingMore = false;
    },

    setChatListError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.isLoadingMore = false;
      state.error = action.payload;
    },

    resetChatList: () => initialChatListState,
  },
});

// ============================================
// Actions
// ============================================

export const {
  setChatListLoading,
  setChatListLoadingMore,
  setChatList,
  appendChatList,
  setChatListError,
  resetChatList,
} = chatListSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const chatListReducer = chatListSlice.reducer;
